import * as React from 'react';

import { usePublishedPage } from '@/domains/cms/api';
import { CmsPage, CmsPageSkeleton } from '@/domains/cms/cms-page';

/**
 * The home page, rendered from the CMS (`CMS-017`).
 *
 * ## What this file used to be
 *
 * Its copy lived here, in JSX, from the day the shell was built — and it carried a header saying
 * so: *"PLACEHOLDER CONTENT — decision of record (2026-08-01): every marketing page is CMS-driven
 * from the first page. … If you are editing words in this file, stop — put them in the CMS."*
 *
 * ADR 022 predicted exactly this and rejected it in advance: *"the pages get built as React
 * components, the copy lives in JSX, and the 'later' migration is a rewrite of every page plus a
 * content re-entry exercise."* It happened anyway, one page deep, because the marketing site was
 * never staged while everything else was. Correcting it at one page cost a seed and this file;
 * correcting it at six would have cost six.
 *
 * The copy is unchanged — `scripts/seed-marketing-home.ts` transcribes it verbatim. `CMS-017`
 * moves the words, it does not edit them. Any wording change from here is an editor's decision
 * made in the console, which is the whole capability being bought.
 *
 * ## Why there is no fallback copy
 *
 * A hardcoded fallback would be the trap growing back: two sources for one page, the JSX one
 * quietly authoritative whenever the CMS is slow or empty, and nobody noticing which one visitors
 * actually saw. If the home page is not published, that is a content problem with a content fix,
 * and the page says so rather than papering over it.
 */
export function HomePage(): React.ReactElement {
  const page = usePublishedPage('home');

  if (page.isLoading) return <CmsPageSkeleton />;
  if (page.data !== undefined) return <CmsPage page={page.data} />;

  return (
    <div className="mx-auto max-w-xl px-6 py-24 text-center">
      <h1 className="text-2xl font-bold tracking-tight">The home page has not been published</h1>
      <p className="mt-4 text-muted-foreground">
        Publish the <code className="text-sm">home</code> page in the console and it will appear
        here.
      </p>
    </div>
  );
}
